import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, InputNumber, message, Space, Tag, Switch, Radio } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { customFieldApi, CreateCustomFieldRequest, UpdateCustomFieldRequest } from '../api/custom_field';
import { CustomField, CustomParamScenario } from '../api/model';

interface CustomFieldComponentsProps {
  scenarioLabels: Record<CustomParamScenario, string>;
  loading: boolean;
}

const CustomFieldComponents: React.FC<CustomFieldComponentsProps> = ({ scenarioLabels, loading }) => {
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [fieldModalVisible, setFieldModalVisible] = useState(false);
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const [fieldForm] = Form.useForm();

  // 获取自定义字段列表
  const fetchCustomFields = async () => {
    try {
      const response = await customFieldApi.listCustomFields();
      if (response.success) {
        setCustomFields(response.data);
      } else {
        message.error(`获取自定义字段失败: ${response.message}`);
      }
    } catch (error) {
      message.error('获取自定义字段失败');
    }
  };

  // 组件加载时获取自定义字段
  useEffect(() => {
    fetchCustomFields();
  }, []);

  // 处理创建自定义字段
  const handleCreateField = () => {
    setEditingField(null);
    fieldForm.resetFields();
    setFieldModalVisible(true);
  };

  // 处理编辑自定义字段
  const handleEditField = (field: CustomField) => {
    setEditingField(field);
    fieldForm.setFieldsValue({
      name: field.name,
      hint: field.hint,
      scenario: field.scenario,
      order: field.order,
      extra: field.extra ? JSON.stringify(field.extra, null, 2) : '{}',
      is_enabled: field.is_enabled,
      visible_to_customer: field.visible_to_customer,
      is_multiline: field.is_multiline
    });
    setFieldModalVisible(true);
  };

  // 处理自定义字段启用状态变更
  const handleFieldEnabledChange = async (field: CustomField, checked: boolean) => {
    try {
      const response = await customFieldApi.setCustomFieldEnabled(field.id, checked);
      if (response.success) {
        message.success('更新自定义字段启用状态成功');
        // 重新获取自定义字段
        await fetchCustomFields();
      } else {
        message.error('更新自定义字段启用状态失败: ' + response.message);
      }
    } catch (error) {
      console.error('更新自定义字段启用状态失败:', error);
      message.error('更新自定义字段启用状态失败');
    }
  };

  // 保存自定义字段
  const handleSaveField = async () => {
    try {
      const values = await fieldForm.validateFields();
      
      // 解析extra字段为JSON对象
      let extraObj: Record<string, any> | undefined;
      if (values.extra) {
        try {
          extraObj = JSON.parse(values.extra);
        } catch (e) {
          message.error('额外信息格式错误，请输入有效的JSON');
          return;
        }
      }
      
      if (editingField) {
        // 更新自定义字段
        const updateRequest: UpdateCustomFieldRequest = {
          name: values.name,
          hint: values.hint,
          order: values.order,
          extra: extraObj,
          visible_to_customer: values.visible_to_customer,
          is_multiline: values.is_multiline
        };
        const response = await customFieldApi.updateCustomField(editingField.id, updateRequest);
        
        if (response.success) {
          message.success('更新成功');
          setFieldModalVisible(false);
          fieldForm.resetFields();
          // 重新获取自定义字段
          await fetchCustomFields();
        } else {
          message.error('更新失败: ' + response.message);
        }
      } else {
        // 创建自定义字段
        const createRequest: CreateCustomFieldRequest = {
          name: values.name,
          hint: values.hint,
          scenario: values.scenario,
          order: values.order,
          extra: extraObj,
          is_enabled: values.is_enabled,
          visible_to_customer: values.visible_to_customer,
          is_multiline: values.is_multiline
        };
        const response = await customFieldApi.createCustomField(createRequest);
        
        if (response.success) {
          message.success('创建成功');
          setFieldModalVisible(false);
          fieldForm.resetFields();
          // 重新获取自定义字段
          await fetchCustomFields();
        } else {
          message.error('创建失败: ' + response.message);
        }
      }
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败');
    }
  };

  // 自定义字段表格列定义
  const getFieldColumns = () => [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '提示信息',
      dataIndex: 'hint',
      key: 'hint',
    },
    {
      title: '适用场景',
      dataIndex: 'scenario',
      key: 'scenario',
      render: (scenario: CustomParamScenario) => (
        <Tag color="blue">{scenarioLabels[scenario]}</Tag>
      ),
    },
    {
      title: '显示顺序',
      dataIndex: 'order',
      key: 'order',
    },
    {
      title: '状态',
      dataIndex: 'is_enabled',
      key: 'is_enabled',
      render: (is_enabled: boolean, record: CustomField) => (
        <Switch 
          checked={is_enabled} 
          onChange={(checked) => handleFieldEnabledChange(record, checked)}
          checkedChildren="启用" 
          unCheckedChildren="禁用" 
        />
      ),
    },
    {
      title: '对患者可见',
      dataIndex: 'visible_to_customer',
      key: 'visible_to_customer',
      render: (visibleToCustomer: boolean) => (
        <Tag color={visibleToCustomer ? 'green' : 'red'}>
          {visibleToCustomer ? '可见' : '不可见'}
        </Tag>
      ),
    },
    {
      title: '多行文本',
      dataIndex: 'is_multiline',
      key: 'is_multiline',
      render: (isMultiline: boolean) => (
        <Tag color={isMultiline ? 'green' : 'red'}>
          {isMultiline ? '是' : '否'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: CustomField) => (
        <Space>
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={() => handleEditField(record)}
            size="small"
          >
            编辑
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      {/* 自定义字段列表 */}
      <Card 
        title="自定义字段"
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleCreateField}
          >
            添加自定义字段
          </Button>
        }
        style={{ marginTop: 24 }}
      >
        <Table 
          columns={getFieldColumns()} 
          dataSource={customFields} 
          loading={loading}
          pagination={false}
          rowKey="id"
        />
      </Card>

      <Modal
        title={editingField ? "编辑自定义字段" : "创建自定义字段"}
        open={fieldModalVisible}
        onOk={handleSaveField}
        onCancel={() => {
          setFieldModalVisible(false);
          fieldForm.resetFields();
        }}
        okText="保存"
        cancelText="取消"
      >
        <Form form={fieldForm} initialValues={{is_enabled: true}} layout="vertical">
          <Form.Item 
            name="name" 
            label="名称" 
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="hint" label="提示信息">
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="scenario" label="适用场景" rules={[{ required: true, message: '请选择适用场景' }]}>  
            <Radio.Group>
              {Object.entries(scenarioLabels).map(([scenario, label]) => (
                <Radio key={scenario} value={scenario as CustomParamScenario}>
                  {label}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
          <Form.Item name="order" label="显示顺序" initialValue={0}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="extra" label="额外信息（JSON格式）">
            <Input.TextArea rows={4} placeholder='请输入JSON格式的额外信息，例如：{"key": "value"}' />
          </Form.Item>
          {editingField === null && (
            <Form.Item 
              name="is_enabled" 
              label="是否启用" 
              valuePropName="checked"
            >
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          )}
          <Form.Item 
            name="visible_to_customer" 
            label="对患者可见" 
            valuePropName="checked"
            initialValue={false}
          >
            <Switch checkedChildren="可见" unCheckedChildren="不可见" />
          </Form.Item>
          <Form.Item 
            name="is_multiline" 
            label="多行文本" 
            valuePropName="checked"
            initialValue={false}
          >
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CustomFieldComponents;